// store/studyYears/thunks.ts

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetStudyYearChangesResponse,
  GetStudyYearsRequest,
  UpdateStudyYearRequest,
  PublishStudyYearRequest,
  AddStudyYearSegmentRequest,
  UpdateStudyYearForecastsRequest,
  DeleteStudyYearSegmentRequest,
  AddStudyYearCompanyRequest,
  DeleteStudyYearCompanyRequest,
} from '@/types/study-years';
import { studyYearsService } from '../../services/study-year.service';
import { AppDispatch, RootState } from '../index';
import { showSnackbar } from '../snakebar/slice';
// --------------------------------------- CORE STUDY YEARS ---------------------------------------

export const getStudyYears = createAsyncThunk(
  'studyYears/getStudyYears',
  async (params: GetStudyYearsRequest | undefined, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYears(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch study years');
    }
  }
);

export const getStudyYearById = createAsyncThunk(
  'studyYears/getStudyYearById',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearById(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch study year');
    }
  }
);

export const updateStudyYear = createAsyncThunk(
  'studyYears/updateStudyYear',
  async (request: UpdateStudyYearRequest, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.updateStudyYear(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update study year');
    }
  }
);

// --------------------------------- STUDY YEAR MANAGEMENT ---------------------------------

export const publishStudyYear = createAsyncThunk(
  'studyYears/publishStudyYear',
  async (
    params: { studyYearId: string; request: PublishStudyYearRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await studyYearsService.publishStudyYear(params.studyYearId, params.request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to publish study year');
    }
  }
);

export const getStudyYearPublications = createAsyncThunk(
  'studyYears/getStudyYearPublications',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearPublications(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year publications'
      );
    }
  }
);

export const getStudyYearChanges = createAsyncThunk(
  'studyYears/getStudyYearChanges',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearChanges(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch study year changes');
    }
  }
);

export const downloadStudyYearExcel = createAsyncThunk(
  'studyYears/downloadStudyYearExcel',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const blob = await studyYearsService.downloadStudyYearExcel(studyYearId);

      // Handle the download directly here
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `study-year-${studyYearId}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Return serializable data instead of the Blob
      return {
        success: true,
        message: 'Excel file downloaded successfully',
        studyYearId,
        downloadedAt: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to download Excel file');
    }
  }
);

// ----------------------------------- SEGMENTS MANAGEMENT -----------------------------------

export const getStudyYearSegments = createAsyncThunk(
  'studyYears/getStudyYearSegments',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearSegments(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year segments'
      );
    }
  }
);

export const addStudyYearSegment = createAsyncThunk(
  'studyYears/addStudyYearSegment',
  async (
    params: { studyYearId: string; request: AddStudyYearSegmentRequest },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await studyYearsService.addStudyYearSegment(
        params.studyYearId,
        params.request
      );
      dispatch(
        showSnackbar({
          message: 'Successfully added segment!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to add study year segment',
          variant: 'error',
        })
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to add study year segment');
    }
  }
);

export const getStudyYearCategories = createAsyncThunk(
  'studyYears/getStudyYearCategories',
  async (params: { studyYearId: string }, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearCategories(params.studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year categories'
      );
    }
  }
);

export const deleteStudyYearSegment = createAsyncThunk(
  'studyYears/deleteStudyYearSegment',
  async (
    params: {
      studyYearId: string;
      studyYearCategoryId: string;
      request: DeleteStudyYearSegmentRequest;
    },
    { rejectWithValue }
  ) => {
    try {
      await studyYearsService.deleteStudyYearSegment(
        params.studyYearId,
        params.studyYearCategoryId,
        params.request
      );
      return { studyYearId: params.studyYearId, studyYearCategoryId: params.studyYearCategoryId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete study year segment'
      );
    }
  }
);

// ---------------------------------- COMPANIES MANAGEMENT ----------------------------------

export const getStudyYearCompanies = createAsyncThunk(
  'studyYears/getStudyYearCompanies',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearCompanies(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year companies'
      );
    }
  }
);

export const addStudyYearCompany = createAsyncThunk(
  'studyYears/addStudyYearCompany',
  async (
    params: { studyYearId: string; request: AddStudyYearCompanyRequest },
    { rejectWithValue }
  ) => {
    console.log(params);
    try {
      const response = await studyYearsService.addStudyYearCompany(
        params.studyYearId,
        params.request
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add study year company');
    }
  }
);

export const deleteStudyYearCompany = createAsyncThunk(
  'studyYears/deleteStudyYearCompany',
  async (
    params: {
      studyYearId: string;
      studyYearCompanyId: string;
      request: DeleteStudyYearCompanyRequest;
    },
    { rejectWithValue }
  ) => {
    try {
      await studyYearsService.deleteStudyYearCompany(
        params.studyYearId,
        params.studyYearCompanyId,
        params.request
      );
      return { studyYearId: params.studyYearId, studyYearCompanyId: params.studyYearCompanyId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete study year company'
      );
    }
  }
);

// ---------------------------------- FORECASTS MANAGEMENT ----------------------------------

export const getStudyYearForecasts = createAsyncThunk(
  'studyYears/getStudyYearForecasts',
  async (studyYearId: string, { rejectWithValue }) => {
    try {
      const response = await studyYearsService.getStudyYearForecasts(studyYearId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year forecasts'
      );
    }
  }
);

export const updateStudyYearForecasts = createAsyncThunk(
  'studyYears/updateStudyYearForecasts',
  async (
    params: { studyYearId: string; request: UpdateStudyYearForecastsRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await studyYearsService.updateStudyYearForecasts(
        params.studyYearId,
        params.request
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update study year forecasts'
      );
    }
  }
);

// ------------------------------------ GROUPED EXPORTS ------------------------------------

export const studyYears = {
  // Core
  getStudyYears,
  getStudyYearById,
  updateStudyYear,

  // Management
  publishStudyYear,
  getStudyYearPublications,
  getStudyYearChanges,
  downloadStudyYearExcel,

  // Segments
  getStudyYearSegments,
  addStudyYearSegment,
  getStudyYearCategories,
  deleteStudyYearSegment,

  // Companies
  getStudyYearCompanies,
  addStudyYearCompany,
  deleteStudyYearCompany,

  // Forecasts
  getStudyYearForecasts,
  updateStudyYearForecasts,
};
